import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Bell, Bot, ChevronDown, ChevronUp, ClipboardList, Loader2, SendHorizontal, X, Zap } from 'lucide-react';


export const GlobalOpsAssistant = ({
  api,
  headers,
  lang,
  roleLabel = '',
  userRole = 'admin',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('sentinel');
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [alerts, setAlerts] = useState(null);
  const [brief, setBrief] = useState(null);
  const [digestLines, setDigestLines] = useState([]);
  const [digestCollapsed, setDigestCollapsed] = useState(false);
  const [snapshotGeneratedAt, setSnapshotGeneratedAt] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [codeAiStatus, setCodeAiStatus] = useState(null);
  const [activatingCodeAi, setActivatingCodeAi] = useState(false);
  const [inboundEvents, setInboundEvents] = useState([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentsOpen, setIncidentsOpen] = useState(true);
  const [incidentTitle, setIncidentTitle] = useState('');
  const [incidentNote, setIncidentNote] = useState('');
  const [incidentBusy, setIncidentBusy] = useState(false);
  const [relatedAlertId, setRelatedAlertId] = useState('');
  const chatEndRef = useRef(null);

  const langKey = lang === 'en' ? 'en' : 'es';
  const isPluka = userRole === 'pluka';

  const loadCodeAiStatus = async () => {
    try {
      const data = await fetch(`${api}/api/pluka/code-ai/status`, { headers }).then(r => r.ok ? r.json() : null);
      if (data) setCodeAiStatus(data);
    } catch {}
  };

  const loadHistory = async () => {
    try {
      const data = await fetch(`${api}/api/pluka/ai-assistant/history?mode=${mode}`, { headers }).then(r => r.json());
      setHistory(data.messages || []);
      if (data?.code_ai_status) setCodeAiStatus(data.code_ai_status);
    } catch {}
  };

  const loadIncidents = async () => {
    try {
      const res = await fetch(`${api}/api/pluka/ops/incidents?limit=8`, { headers });
      const data = res.ok ? await res.json() : {};
      setIncidents(data.incidents || []);
    } catch {
      setIncidents([]);
    }
  };

  const loadInboundEvents = async () => {
    setInboundLoading(true);
    try {
      const res = await fetch(`${api}/api/pluka/ops/inbound-events?limit=20`, { headers });
      const data = res.ok ? await res.json() : {};
      setInboundEvents(data.events || []);
    } catch {
      setInboundEvents([]);
    }
    setInboundLoading(false);
  };

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const briefBody = JSON.stringify({ lang: langKey });
      const [alertData, briefData] = await Promise.all([
        fetch(`${api}/api/pluka/monitor/alerts`, { headers }).then(r => r.json()),
        fetch(`${api}/api/pluka/ai-sentinel/brief`, { method: 'POST', headers, body: briefBody }).then(r => r.ok ? r.json() : null),
      ]);
      setAlerts(alertData);
      if (briefData?.digest_lines?.length) {
        setDigestLines(briefData.digest_lines);
      } else {
        setDigestLines([]);
      }
      if (briefData?.snapshot?.generated_at) {
        setSnapshotGeneratedAt(briefData.snapshot.generated_at);
      }
      if (briefData?.brief) setBrief(briefData.brief);
      else setBrief(null);
    } catch {}
    setAlertsLoading(false);
  };

  useEffect(() => {
    if (open) {
      loadHistory();
      loadAlerts();
      loadCodeAiStatus();
      loadInboundEvents();
      loadIncidents();
    }
  }, [open, mode]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const formatSnapshotTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(langKey === 'en' ? 'en-GB' : 'es-ES', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '';
    }
  };

  const saveIncident = async (alertId) => {
    const t = (incidentTitle || '').trim();
    if (!t) return;
    setIncidentBusy(true);
    try {
      const res = await fetch(`${api}/api/pluka/ops/incidents`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: t.slice(0, 300),
          note: (incidentNote || '').trim().slice(0, 4000),
          related_alert_id: (alertId || relatedAlertId || '').toString().slice(0, 80),
        }),
      });
      if (res.ok) {
        setIncidentTitle('');
        setIncidentNote('');
        setRelatedAlertId('');
        loadIncidents();
      }
    } catch {}
    setIncidentBusy(false);
  };

  const closeIncident = async (id) => {
    if (!id) return;
    setIncidentBusy(true);
    try {
      const res = await fetch(`${api}/api/pluka/ops/incidents/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'closed' }),
      });
      if (res.ok) loadIncidents();
    } catch {}
    setIncidentBusy(false);
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    setLoading(true);
    const payload = { message, mode, lang: langKey, conversation: history.slice(-12).map(item => ({ role: item.role, content: item.content })) };
    const userText = message;
    try {
      const res = await fetch(`${api}/api/pluka/ai-assistant`, { method: 'POST', headers, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (data?.code_ai_status) setCodeAiStatus(data.code_ai_status);
      if (data?.snapshot_generated_at) setSnapshotGeneratedAt(data.snapshot_generated_at);
      let assistantText = (data.reply || data.message || '').trim();
      if (!res.ok) {
        const detail = typeof data?.detail === 'string' ? data.detail : JSON.stringify(data?.detail || '');
        assistantText = langKey === 'en'
          ? `Could not complete the request (${res.status}). ${detail || 'Check session and try again.'}`
          : `No se pudo completar la peticion (${res.status}). ${detail || 'Revisa la sesion e intentalo de nuevo.'}`;
      } else if (!assistantText) {
        assistantText = langKey === 'en'
          ? 'No text came back. Open Admin settings and check that the AI assistant is connected and has a valid key, or that the service has enough credit.'
          : 'No llegó texto de respuesta. Revisa Ajustes del panel: que el asistente tenga conexión y clave valida, o crédito en el proveedor.';
      }
      const nextHistory = [
        ...history,
        { id: `local-user-${Date.now()}`, role: 'user', mode, content: userText },
        { id: `local-ai-${Date.now()}`, role: 'assistant', mode, content: assistantText },
      ];
      setHistory(nextHistory);
      setMessage('');
    } catch (e) {
      const errMsg = langKey === 'en'
        ? `Connection error: ${e?.message || 'unknown'}. Check network and try again.`
        : `Error de conexion: ${e?.message || 'desconocido'}. Revisa red e intentalo de nuevo.`;
      setHistory([
        ...history,
        { id: `local-user-${Date.now()}`, role: 'user', mode, content: userText },
        { id: `local-ai-${Date.now()}`, role: 'assistant', mode, content: errMsg },
      ]);
      setMessage('');
    }
    setLoading(false);
  };

  const toggleCodeAi = async (enabled) => {
    setActivatingCodeAi(true);
    try {
      const data = await fetch(`${api}/api/pluka/code-ai/activate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ enabled }),
      }).then(r => r.ok ? r.json() : null);
      if (data?.status) {
        setCodeAiStatus(data.status);
      }
      if (!enabled && mode === 'code_ai') {
        setMode('sentinel');
      }
    } catch {}
    setActivatingCodeAi(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const quickPromptsPlukaEs = [
    'Que colas o revisiones tocar primero hoy?',
    'Resumen de riesgos legales o fiscales segun datos actuales',
    'Donde miro en el panel si hay problemas con liquidaciones o KYC?',
  ];
  const quickPromptsPlukaEn = [
    'Which queues should I tackle first today?',
    'Legal or fiscal risks from current data',
    'Where in the dashboard do I check payouts or KYC issues?',
  ];
  const quickPromptsDirEs = [
    'Cual es la prioridad estrategica hoy con estos numeros?',
    'Mayor riesgo legal o fiscal que deba decidir',
    'Que validar en negocio antes de tocar codigo con Code AI?',
  ];
  const quickPromptsDirEn = [
    'Top strategic priority given these numbers?',
    'Main legal or fiscal risk I should decide on',
    'What to validate in the business before using Code AI?',
  ];

  const quickPrompts = (() => {
    if (langKey === 'en') return isPluka ? quickPromptsPlukaEn : quickPromptsDirEn;
    return isPluka ? quickPromptsPlukaEs : quickPromptsDirEs;
  })();

  const highAlerts = alerts?.by_severity?.high || 0;
  const totalAlerts = alerts?.total_alerts || 0;
  const canUseCodeAi = Boolean(codeAiStatus?.eligible);
  const codeAiActive = Boolean(codeAiStatus?.active);

  return (
    <>
      <button
        data-testid="global-ops-assistant-toggle"
        onClick={() => setOpen(value => !value)}
        className="fixed bottom-28 right-5 z-40 rounded-full bg-pulse-primary text-black shadow-2xl px-4 py-3 inline-flex items-center gap-2 font-bold hover:scale-[1.02] transition-transform duration-200"
      >
        {open ? <X size={18} /> : <Zap size={18} />}
        <span className="hidden sm:inline">Pulse Sentinel</span>
        {!open && highAlerts > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">{highAlerts}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="global-ops-assistant-panel"
          className="fixed bottom-44 right-5 z-40 w-[min(460px,calc(100vw-32px))] rounded-3xl border border-pulse-border bg-[#060a18]/97 backdrop-blur-xl shadow-2xl flex flex-col min-h-0 max-h-[min(85dvh,calc(100vh-96px))]"
        >
          <div className="shrink-0 z-20 border-b border-pulse-border bg-[#060a18]/98 px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-pulse-muted">Inteligencia Operativa Multi-Rol</p>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading font-bold text-base inline-flex items-center gap-2"><Zap size={15} className="text-yellow-400" /> Pulse Sentinel</h3>
                  {roleLabel ? (
                    <span className="text-[9px] px-2 py-0.5 rounded-full border border-pulse-border text-pulse-muted font-semibold">{roleLabel}</span>
                  ) : null}
                </div>
                {mode === 'sentinel' && snapshotGeneratedAt ? (
                  <p className="text-[10px] text-pulse-muted/90 mt-1" data-testid="sentinel-snapshot-time">
                    {langKey === 'en' ? 'Data updated:' : 'Datos actualizados:'}{' '}
                    {formatSnapshotTime(snapshotGeneratedAt)} UTC
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {totalAlerts > 0 && (
                  <span data-testid="sentinel-alert-badge" className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-bold">{totalAlerts} {langKey === 'en' ? 'alerts' : 'alertas'}</span>
                )}
                <button data-testid="global-ops-assistant-close" onClick={() => setOpen(false)} className="rounded-full border border-pulse-border p-1.5 text-pulse-muted hover:text-white transition"><X size={14} /></button>
              </div>
            </div>
          </div>

          <div className="shrink-0 z-20 px-4 py-3 border-b border-pulse-border bg-[#060a18]/98 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setMode('sentinel')}
                className={`text-xs px-3 py-1 rounded-full border ${mode === 'sentinel' ? 'bg-yellow-500 text-black border-yellow-400' : 'bg-transparent text-pulse-muted border-pulse-border'}`}
              >
                Sentinel
              </button>
              <button
                type="button"
                onClick={() => canUseCodeAi && setMode('code_ai')}
                disabled={!canUseCodeAi}
                className={`text-xs px-3 py-1 rounded-full border ${mode === 'code_ai' ? 'bg-cyan-400 text-black border-cyan-300' : 'bg-transparent text-pulse-muted border-pulse-border'} ${!canUseCodeAi ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Code AI
              </button>
              {canUseCodeAi && (
                <button
                  type="button"
                  disabled={activatingCodeAi}
                  onClick={() => toggleCodeAi(!codeAiActive)}
                  className={`text-xs px-3 py-1 rounded-full border ${codeAiActive ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' : 'bg-white/[0.03] text-pulse-muted border-pulse-border'} ${activatingCodeAi ? 'opacity-60' : ''}`}
                >
                  {activatingCodeAi ? '...' : (codeAiActive ? 'Code AI ON' : 'Code AI OFF')}
                </button>
              )}
            </div>
            {mode === 'code_ai' ? (
              <p className="text-[11px] text-cyan-200/80">
                {codeAiActive
                  ? (langKey === 'en'
                    ? 'Live platform + Git repo. Type naturally or use /run, /read, /write, /append.'
                    : 'Copiloto con datos en vivo + repo Git. Escribe en natural o usa /run, /read, /write, /append.')
                  : (codeAiStatus?.reason || (langKey === 'en' ? 'Turn on Code AI for live copilot and repo commands.' : 'Activa Code AI para el copiloto en vivo y comandos sobre el repo.'))}
              </p>
            ) : (
              <p className="text-[11px] text-pulse-muted">
                {langKey === 'en'
                  ? 'Summaries in everyday words: law, money, and how the site runs. No dev jargon. To change code, use Code AI (director).'
                  : 'Resumenes en palabras sencillas: legal, dinero, operación del sitio. Sin tecnicismos de programación. Cambios de codigo solo con Code AI (directora).'}
              </p>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y">
          {mode === 'sentinel' && digestLines.length > 0 && (
            <div className="px-4 py-2 border-b border-pulse-border bg-white/[0.02] shrink-0" data-testid="sentinel-digest">
              <button
                type="button"
                onClick={() => setDigestCollapsed(c => !c)}
                className="flex items-center justify-between w-full text-left text-[10px] uppercase tracking-wider text-pulse-muted font-bold mb-1"
              >
                {langKey === 'en' ? 'Moment summary' : 'Resumen del momento'}
                {digestCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              </button>
              {!digestCollapsed && (
                <ul className="text-[11px] text-slate-300/95 space-y-1 leading-snug">
                  {digestLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {mode === 'sentinel' && (brief || (alerts && highAlerts > 0)) && (
            <div className="px-4 py-3 border-b border-pulse-border bg-red-500/5 shrink-0" data-testid="sentinel-proactive-brief">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs">
                  {brief ? (
                    <p className="text-amber-200/90 leading-relaxed">{brief}</p>
                  ) : (
                    <div className="space-y-1">
                      {alerts?.alerts?.filter(a => a.severity === 'high').slice(0, 3).map((a, i) => (
                        <p key={i} className="text-red-300/90">{a.title}: {a.detail}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {mode === 'sentinel' && alerts?.alerts?.length > 0 && (() => {
            const highFirst = (alerts.alerts || []).filter((a) => a.severity === 'high').slice(0, 2);
            const show = highFirst.length ? highFirst : (alerts.alerts || []).slice(0, 2);
            if (!show.length) return null;
            return (
              <div className="px-4 py-2.5 border-b border-pulse-border bg-amber-500/5 shrink-0" data-testid="sentinel-priority-cards">
                <p className="text-[10px] uppercase tracking-wider text-amber-200/80 font-bold mb-2">
                  {langKey === 'en' ? 'Who, impact, and where to act' : 'Quién, impacto y dónde actuar'}
                </p>
                <ul className="space-y-2.5 text-[10px] text-slate-200/95 leading-snug">
                  {show.map((a) => {
                    const imp = langKey === 'en' ? (a.impact_line_en || a.impact_line_es) : a.impact_line_es;
                    const wh = langKey === 'en' ? (a.where_to_act_en || a.where_to_act_es) : a.where_to_act_es;
                    const own = langKey === 'en' ? (a.owner_en || a.owner_es) : a.owner_es;
                    return (
                      <li key={a.id || a.title} className="border-l-2 border-amber-500/50 pl-2.5">
                        <span className="font-bold text-amber-100/95">{a.title?.slice(0, 200)}</span>
                        {imp && <p className="text-pulse-muted/95 mt-0.5">{(langKey === 'en' ? 'Impact' : 'Impacto')}: {imp}</p>}
                        {own && <p className="text-cyan-200/80">{(langKey === 'en' ? 'Owner' : 'Quien')}: {own}</p>}
                        {wh && <p className="text-slate-300/90">{(langKey === 'en' ? 'Next step' : 'Siguiente')}: {wh}</p>}
                        {a.id && (
                          <button
                            type="button"
                            onClick={() => { setRelatedAlertId(a.id || ''); setIncidentTitle((a.title || 'Seguimiento').slice(0, 200)); setIncidentNote((a.detail || '').slice(0, 2000)); }}
                            className="mt-1 text-[9px] text-cyan-300 underline"
                          >
                            {langKey === 'en' ? 'Start incident note from this alert' : 'Abrir nota de incidente con este aviso'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })()}

          {mode === 'sentinel' && (
            <div className="px-4 py-2.5 border-b border-pulse-border bg-violet-500/5 shrink-0" data-testid="sentinel-incidents">
              <button
                type="button"
                onClick={() => setIncidentsOpen((o) => !o)}
                className="flex items-center justify-between w-full text-left text-[10px] uppercase tracking-wider text-violet-200/90 font-bold mb-1.5"
              >
                <span className="inline-flex items-center gap-1.5"><ClipboardList size={12} /> {langKey === 'en' ? 'Incident log' : 'Registro de incidencias'}</span>
                {incidentsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {incidentsOpen && (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    {incidents.filter((i) => i.status !== 'closed').slice(0, 5).map((i) => (
                      <div key={i.id} className="flex items-start justify-between gap-2 text-[10px] text-slate-200/90 border-b border-pulse-border/30 pb-1.5 last:border-0 last:pb-0">
                        <div>
                          <span className="font-medium">{i.title}</span>
                          {i.note ? <p className="text-pulse-muted text-[9px] mt-0.5 line-clamp-2">{i.note}</p> : null}
                        </div>
                        <button type="button" onClick={() => closeIncident(i.id)} disabled={incidentBusy} className="shrink-0 text-[9px] text-violet-300 hover:text-white disabled:opacity-50">
                          {langKey === 'en' ? 'Close' : 'Cerrar'}
                        </button>
                      </div>
                    ))}
                    {!incidents.filter((i) => i.status !== 'closed').length && (
                      <p className="text-[9px] text-pulse-muted">{langKey === 'en' ? 'No open items.' : 'Nada abierto aún.'}</p>
                    )}
                  </div>
                  <div className="pt-1 space-y-1">
                    <input
                      type="text"
                      value={incidentTitle}
                      onChange={(e) => setIncidentTitle(e.target.value)}
                      placeholder={langKey === 'en' ? 'Short title (e.g. delayed payouts)' : 'Título breve (ej. retraso en apoyos)'}
                      className="w-full rounded-lg border border-pulse-border bg-pulse-elevated/80 px-2 py-1.5 text-[10px] text-white placeholder:text-pulse-muted/60"
                    />
                    <textarea
                      value={incidentNote}
                      onChange={(e) => setIncidentNote(e.target.value)}
                      placeholder={langKey === 'en' ? 'What you did, who you informed…' : 'Qué hiciste, a quién avisaste…'}
                      rows={2}
                      className="w-full rounded-lg border border-pulse-border bg-pulse-elevated/80 px-2 py-1.5 text-[10px] text-white placeholder:text-pulse-muted/60 resize-none"
                    />
                    <button
                      type="button"
                      onClick={() => saveIncident(relatedAlertId)}
                      disabled={incidentBusy || !(incidentTitle || '').trim()}
                      className="w-full rounded-lg bg-violet-500/20 border border-violet-400/40 text-violet-100 text-[10px] font-bold py-1.5 disabled:opacity-50"
                    >
                      {incidentBusy ? (langKey === 'en' ? 'Saving…' : 'Guardando…') : (langKey === 'en' ? 'Save to log' : 'Guardar en el registro')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
            <div className="px-4 py-2 border-b border-pulse-border bg-white/[0.01] shrink-0 flex gap-2 flex-wrap" data-testid="sentinel-alerts-summary">
              {alerts.alerts.slice(0, 5).map((a, i) => (
                <span key={i} className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${
                  a.severity === 'high' ? 'bg-red-500/15 text-red-400' :
                  a.severity === 'medium' ? 'bg-amber-500/15 text-amber-400' :
                  'bg-slate-500/15 text-slate-400'
                }`}>{a.category}: {a.title.split(':')[0]}</span>
              ))}
            </div>
          )}

          {mode === 'sentinel' && (inboundLoading || inboundEvents.length > 0) && (
            <div className="px-4 py-2 border-b border-pulse-border bg-cyan-500/5 shrink-0" data-testid="sentinel-inbound-feed">
              <p className="text-[10px] uppercase tracking-wider text-cyan-300/90 font-bold mb-1.5 flex items-center gap-1.5">
                <Bell size={12} className="shrink-0" />
                {langKey === 'en' ? 'External feed (Git / uptime / webhooks)' : 'Canal externo (Git / uptime / webhooks)'}
              </p>
              {inboundLoading && !inboundEvents.length ? (
                <p className="text-[11px] text-pulse-muted">{langKey === 'en' ? 'Loading…' : 'Cargando…'}</p>
              ) : (
                <ul className="text-[10px] text-slate-300/95 space-y-1.5 max-h-28 overflow-y-auto">
                  {inboundEvents.map((ev) => (
                    <li key={ev.id} className="border-l-2 border-cyan-500/30 pl-2">
                      <span className="text-cyan-400/80 font-mono">[{ev.source}]</span>{' '}
                      <span className="text-pulse-muted">{(ev.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                      <br />
                      {ev.summary || ev.event_type}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {mode === 'sentinel' && (
            <div className="px-4 py-2 border-b border-pulse-border bg-white/[0.01] shrink-0 flex flex-wrap gap-1.5" data-testid="sentinel-quick-prompts">
              {quickPrompts.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMessage(q)}
                  className="text-[10px] px-2.5 py-1 rounded-full border border-pulse-border text-pulse-muted hover:text-yellow-200/90 hover:border-yellow-500/40 transition text-left max-w-full"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="px-4 py-3 space-y-2.5 pb-4" style={{minHeight: '120px'}}>
            {history.length === 0 && !alertsLoading && (
              <div data-testid="global-ops-assistant-empty" className="rounded-2xl border border-dashed border-pulse-border bg-pulse-surface/50 px-4 py-4 text-xs text-pulse-muted space-y-2">
                {mode === 'code_ai' ? (
                  <>
                    <div className="flex items-center gap-2 text-cyan-300 font-bold text-[11px]"><Bot size={13} /> Code AI — copiloto en vivo</div>
                    <p>{langKey === 'en' ? 'Ask in plain language: priorities, risks, what to review in code given live metrics.' : 'Pregunta en lenguaje natural: prioridades, riesgos, que revisar en codigo segun metricas y alertas actuales.'}</p>
                    <p className="text-pulse-muted/70">/run git status --short — /read backend/routes/ops_assistant.py</p>
                    <p className="text-pulse-muted/70">{langKey === 'en' ? 'Director account only (verified).' : 'Exclusivo cuenta directora verificada y activada.'}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-yellow-400 font-bold text-[11px]"><Zap size={13} /> Pulse Sentinel</div>
                    <p>{isPluka
                      ? (langKey === 'en' ? 'Operational focus: queues, compliance, next step in the dashboard.' : 'Enfoque operativo: colas, cumplimiento, siguiente paso en el panel.')
                      : (langKey === 'en' ? 'Executive focus: risk, strategy, trade-offs. Use Code AI to change the repo.' : 'Enfoque direccion: riesgo, estrategia, trade-offs. Code AI para cambiar el repositorio.')}</p>
                    <p className="text-pulse-muted/70">{langKey === 'en' ? 'Check the moment summary above. Data timestamp is in the header.' : 'Mira el resumen del momento arriba. La hora de los datos esta en la cabecera.'}</p>
                  </>
                )}
              </div>
            )}

            {history.map((item, index) => (
              <div key={item.id || `${item.role}-${index}`} data-testid={`global-ops-message-${index}`} className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${item.role === 'assistant' ? 'bg-slate-800/80 text-white border border-slate-700/30' : 'bg-pulse-primary/10 text-pulse-primary border border-pulse-primary/20'}`}>
                {item.role === 'assistant' && <span className="text-[9px] text-yellow-400/60 font-bold block mb-1">SENTINEL</span>}
                {item.content}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          </div>

          <div className="p-3 border-t border-pulse-border bg-white/[0.02] shrink-0">
            <div className="flex gap-2">
              <textarea
                data-testid="global-ops-assistant-input"
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  mode === 'code_ai'
                    ? (langKey === 'en'
                      ? 'What should I prioritize given alerts and repo? or /run git status --short'
                      : 'Que priorizo hoy segun alertas y estado del repo? o /run git status --short')
                    : (langKey === 'en' ? 'Ask Sentinel: legal, fiscal, metrics, strategy...' : 'Pregunta a Sentinel: legal, fiscal, metricas, estrategia...')
                }
                rows={2}
                className="flex-1 rounded-2xl border border-pulse-border bg-pulse-elevated px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500/50 focus:outline-none resize-none"
              />
              <button data-testid="global-ops-assistant-send" onClick={sendMessage} disabled={loading} className="self-end rounded-2xl bg-yellow-500 px-4 py-2.5 text-black font-bold disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-yellow-400 transition">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <SendHorizontal size={15} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
